import React, { useState } from "react"
import { useQuery } from "react-query"
import { ConsultantModel } from "../../../../models"
import axios, { AxiosError } from "axios"
import { toast } from "react-toastify"
import { API_ESPACE_FOURNISSEUR_RESP_GET_COLLABORATEURS_ENDPOINT, CONST_CONSULTANT_ETAT_EN_COURS_EMBAUCHE_POUR_MISSION } from "../../../../constants"
import { Link } from "react-router-dom"
import MyAlert from "../../../../utils/MyAlert"
import { useAuth } from "../../../../modules/auth"
import MyModal from "../../../../utils/MyModal"
import ChangementDeResponsable from "../ChangementDeResponsable"
import moment from "moment/moment"
import clsx from "clsx"

const REACT_QUERY_KEY_GET_LISTING = "REACT_QUERY_KEY_GET_LISTING"

/**
 * Génère une plage de pagination tronquée
 */
const getPaginationRange = (current: number, total: number) => {
     const delta = 1
     const range: (number | string)[] = []
     const rangeWithDots: (number | string)[] = []
     let l: number | string | undefined

     range.push(1)
     for (let i = current - delta; i <= current + delta; i++) {
          if (i < total && i > 1) range.push(i)
     }
     if (total > 1) range.push(total)

     for (const i of range) {
          if (l) {
               if (typeof i === "number" && typeof l === "number") {
                    if (i - l === 2) rangeWithDots.push(l + 1)
                    else if (i - l !== 1) rangeWithDots.push("...")
               }
          }
          rangeWithDots.push(i)
          l = i
     }
     return rangeWithDots
}

const FournisseurRespGetCollaborateurs = () => {
     const { currentUser } = useAuth()
     const [currentPage, setCurrentPage] = useState<number>(1) // Type explicite pour éviter 'never'
     const [searchTerm, setSearchTerm] = useState<string>("")
     const itemsPerPage = 8

     const [changementRespInModal, setChangementRespInModal] = useState<{
          isModalVisible: boolean
          consultants: null | ConsultantModel[]
     }>({ isModalVisible: false, consultants: null })

     const listingQuery = useQuery<ConsultantModel[], AxiosError>(REACT_QUERY_KEY_GET_LISTING, async () => {
          return axios.post(API_ESPACE_FOURNISSEUR_RESP_GET_COLLABORATEURS_ENDPOINT, {}).then(r => r.data)
     })

     const handlePageChange = (pageNumber: number) => setCurrentPage(pageNumber)

     // Notification d'erreur via Toast
     if (listingQuery.isError) {
          toast.error("Erreur de chargement des données")
          return (
               <MyAlert type="danger" classNames="mb-4">
                    <div className="d-flex align-items-center">
                         <span className="me-4">{listingQuery.error.response?.data?.detail || "Erreur inconnue"}</span>
                         <button className="btn btn-sm btn-danger" onClick={() => listingQuery.refetch()}>
                              Réessayer
                         </button>
                    </div>
               </MyAlert>
          )
     }

     if (listingQuery.isFetching)
          return (
               <div className="d-flex flex-column flex-center py-20">
                    <span className="spinner-border text-primary w-40px h-40px mb-4"></span>
                    <span className="text-gray-600 fw-semibold">Récupération des collaborateurs...</span>
               </div>
          )

     const allData = listingQuery.data || []
     const filteredData = allData.filter(item => `${item.prenom} ${item.nom}`.toLowerCase().includes(searchTerm.toLowerCase()))

     const offset = (currentPage - 1) * itemsPerPage
     const currentItems = filteredData.slice(offset, offset + itemsPerPage)
     const pageCount = Math.ceil(filteredData.length / itemsPerPage)
     const paginationRange = getPaginationRange(currentPage, pageCount)

     if (allData.length === 0) {
          return (
               <div className="card card-flush shadow-sm">
                    <div className="card-body d-flex flex-column align-items-center justify-content-center py-20">
                         <i className="fas fa-users text-gray-300 mb-5" style={{ fontSize: "4rem" }}></i>
                         <h4 className="text-gray-600 fw-bold mb-2">Aucun collaborateur</h4>
                         <p className="text-gray-500 fs-6 text-center mb-5">Vous n'avez aucun collaborateur rattaché pour le moment.</p>
                         <button className="btn btn-sm btn-light-primary" onClick={() => listingQuery.refetch()}>
                              <i className="fas fa-sync me-2"></i>Rafraîchir
                         </button>
                    </div>
               </div>
          )
     }

     return (
          <div className="card card-flush shadow-sm">
               {/* HEADER : Recherche Pleine Largeur sur Mobile */}
               <div className="card-header align-items-center py-5 gap-2 gap-md-5">
                    <div className="card-title w-100 w-md-auto">
                         <div className="d-flex align-items-center position-relative my-1 w-100">
                              <i className="fas fa-search fs-4 position-absolute ms-4 text-gray-500"></i>
                              <input
                                   type="text"
                                   className="form-control form-control-solid w-100 w-md-250px ps-14"
                                   placeholder="Rechercher ..."
                                   value={searchTerm}
                                   onChange={e => {
                                        setSearchTerm(e.target.value)
                                        setCurrentPage(1)
                                   }}
                              />
                         </div>
                    </div>
                    {/* Badge + Refresh */}
                    <div className="card-toolbar d-flex gap-3">
                         <button className="btn btn-sm btn-icon btn-light-primary" onClick={() => listingQuery.refetch()} title="Rafraîchir">
                              <i className="fas fa-sync"></i>
                         </button>
                         <span className="badge badge-light-primary fw-bold px-4 py-3 d-none d-md-inline">{filteredData.length} Collaborateurs</span>
                    </div>
               </div>

               <div className="card-body pt-0">
                    {/* --- VUE DESKTOP (TABLEAU) --- */}
                    <div className="d-none d-lg-block table-responsive">
                         <table className="table align-middle table-row-dashed fs-6 gy-5">
                              <thead>
                                   <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                        <th>Collaborateur</th>
                                        <th>Entrée</th>
                                        <th className="text-center">Etat</th>
                                        <th>Responsables</th>
                                        <th className="text-end">Actions</th>
                                   </tr>
                              </thead>
                              <tbody className="fw-semibold text-gray-600">
                                   {currentItems.map(item => (
                                        <tr key={item.id}>
                                             <td className="d-flex align-items-center">
                                                  <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                                                       <div className="symbol-label fs-3 bg-light-primary text-primary">{item.prenom?.charAt(0)}</div>
                                                  </div>
                                                  <div className="d-flex flex-column">
                                                       <Link
                                                            to={`/espace-resp-fournisseur/collaborateurs/${item.id}/details`}
                                                            className="text-gray-800 text-hover-primary mb-1 fw-bold"
                                                       >
                                                            {item.nom} {item.prenom}
                                                       </Link>
                                                       <span className="fs-7 text-muted">{item.entite?.nom}</span>
                                                  </div>
                                             </td>
                                             <td>{item.dateEmbauche?.format("DD/MM/YYYY") || "N/A"}</td>
                                             <td className="text-center">
                                                  <EtatBadge item={item} />
                                             </td>
                                             <td>
                                                  <div className="d-flex flex-column fs-7">
                                                       <div className="text-gray-800">
                                                            <i className="fas fa-user-tie fs-9 me-1"></i> {item.sousTraitant?.fournisseurResponsable?.prenomNom}
                                                       </div>
                                                       <span className="text-muted">
                                                            <i className="fas fa-envelope fs-10 me-1"></i> {item.personnel?.prenomNom}
                                                       </span>
                                                  </div>
                                             </td>
                                             <td className="text-end">
                                                  <ActionsButtons
                                                       item={item}
                                                       currentUser={currentUser}
                                                       onEdit={() => setChangementRespInModal({ isModalVisible: true, consultants: [item] })}
                                                  />
                                             </td>
                                        </tr>
                                   ))}
                              </tbody>
                         </table>
                    </div>

                    {/* --- VUE MOBILE (CARTES) --- */}
                    <div className="d-lg-none mt-5">
                         {currentItems.map(item => (
                              <div key={item.id} className="border border-dashed border-gray-300 rounded p-4 mb-4 bg-light-body">
                                   <div className="d-flex align-items-center mb-4">
                                        <div className="symbol symbol-40px symbol-circle me-3">
                                             <div className="symbol-label bg-light-primary text-primary">{item.prenom?.charAt(0)}</div>
                                        </div>
                                        <div className="flex-grow-1">
                                             <Link to={`/espace-resp-fournisseur/collaborateurs/${item.id}/details`} className="text-gray-800 fw-bold fs-6">
                                                  {item.prenomNom}
                                             </Link>
                                             <div className="text-muted fs-7">{item.entite?.nom}</div>
                                        </div>
                                        <EtatBadge item={item} />
                                   </div>
                                   <div className="d-flex flex-stack fs-7 mb-2">
                                        <span className="text-muted">Responsable :</span>
                                        <span className="text-gray-800 fw-bold text-end">{item.personnel?.prenomNom}</span>
                                   </div>
                                   <div className="d-flex justify-content-end gap-2 mt-4">
                                        <Link to={`/espace-resp-fournisseur/collaborateurs/${item.id}/details`} className="btn btn-sm btn-light-primary flex-grow-1">
                                             Consulter
                                        </Link>
                                        {currentUser?.user?.roles?.includes("ROLE_RESPONSABLE_FOURNISSEUR_ADMIN") && (
                                             <button
                                                  className="btn btn-sm btn-light-warning"
                                                  onClick={() => setChangementRespInModal({ isModalVisible: true, consultants: [item] })}
                                             >
                                                  <i className="fas fa-user-cog"></i>
                                             </button>
                                        )}
                                   </div>
                              </div>
                         ))}
                    </div>

                    {/* --- PAGINATION TRONQUÉE AVEC ICONES --- */}
                    {filteredData.length > itemsPerPage && (
                         <div className="d-flex flex-stack flex-wrap pt-10">
                              <div className="fs-7 fw-bold text-gray-700 mb-4 mb-md-0 d-none d-md-block">
                                   {offset + 1} à {Math.min(offset + itemsPerPage, filteredData.length)} sur {filteredData.length}
                              </div>
                              <ul className="pagination pagination-outline justify-content-center w-100 w-md-auto">
                                   <li className={clsx("page-item first", currentPage === 1 && "disabled")}>
                                        <button className="page-link" onClick={() => handlePageChange(1)}>
                                             <i className="fas fa-angle-double-left"></i>
                                        </button>
                                   </li>
                                   <li className={clsx("page-item previous", currentPage === 1 && "disabled")}>
                                        <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                                             <i className="fas fa-angle-left"></i>
                                        </button>
                                   </li>
                                   {paginationRange.map((p, i) => (
                                        <li key={i} className={clsx("page-item", p === currentPage && "active", p === "..." && "disabled")}>
                                             <button className="page-link" onClick={() => typeof p === "number" && handlePageChange(p)}>
                                                  {p}
                                             </button>
                                        </li>
                                   ))}
                                   <li className={clsx("page-item next", currentPage === pageCount && "disabled")}>
                                        <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                                             <i className="fas fa-angle-right"></i>
                                        </button>
                                   </li>
                                   <li className={clsx("page-item last", currentPage === pageCount && "disabled")}>
                                        <button className="page-link" onClick={() => handlePageChange(pageCount)}>
                                             <i className="fas fa-angle-double-right"></i>
                                        </button>
                                   </li>
                              </ul>
                         </div>
                    )}
               </div>

               {/* MODAL CHANGEMENT DE RESPONSABLE */}
               {changementRespInModal.isModalVisible && (
                    <MyModal
                         title={<>Nouveau responsable pour {changementRespInModal.consultants![0].prenomNom}</>}
                         show={true}
                         handleClose={() => setChangementRespInModal({ isModalVisible: false, consultants: null })}
                    >
                         <ChangementDeResponsable
                              consultants={changementRespInModal.consultants!}
                              onCancelCallback={() => setChangementRespInModal({ isModalVisible: false, consultants: null })}
                              onSuccessCallback={() => {
                                   listingQuery.refetch()
                                   setChangementRespInModal({ isModalVisible: false, consultants: null })
                                   toast.success("Responsable mis à jour avec succès")
                              }}
                         />
                    </MyModal>
               )}
          </div>
     )
}

// --- SOUS-COMPOSANTS ---

const EtatBadge = ({ item }: { item: ConsultantModel }) => {
     const isEnCours = item.etat === CONST_CONSULTANT_ETAT_EN_COURS_EMBAUCHE_POUR_MISSION
     const isSorti = item.dateSortie?.isBefore(moment())
     return (
          <span className={clsx("badge fw-bold", isEnCours ? "badge-light-info" : isSorti ? "badge-light-danger" : "badge-light-success")}>
               {isEnCours ? "En cours" : isSorti ? "Inactif" : "Actif"}
          </span>
     )
}

const ActionsButtons = ({ item, currentUser, onEdit }: any) => (
     <div className="d-flex justify-content-end gap-2">
          <Link to={`/espace-resp-fournisseur/collaborateurs/${item.id}/details`} className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
               <i className="fas fa-eye fs-4"></i>
          </Link>
          {currentUser?.user?.roles?.includes("ROLE_RESPONSABLE_FOURNISSEUR_ADMIN") && (
               <button className="btn btn-icon btn-bg-light btn-active-color-warning btn-sm" onClick={onEdit}>
                    <i className="fas fa-user-cog fs-4"></i>
               </button>
          )}
     </div>
)

export { FournisseurRespGetCollaborateurs }
